import { useCallback, useEffect, useRef, useState } from 'react';

import { apiRequest } from '@/config';
import { logError } from '@/lib/logger';

export type ExecutionState =
  | 'idle'
  | 'creating'
  | 'running'
  | 'completed'
  | 'error';

export interface ExecutionProgress {
  state: ExecutionState;
  progress: number;
  completedCount: number;
  totalExecutions: number;
  error: string | null;
  runId: string | null;
}

interface ExecuteParams {
  promptIds: string[];
  configIds: string[];
  runName: string;
  description?: string;
  multiplicity: number;
  concurrency: number;
  promptSetId?: string | null;
}

export function useExecutionSSE(onSuccess: (runId: string) => void) {
  const [executionProgress, setExecutionProgress] = useState<ExecutionProgress>(
    {
      state: 'idle',
      progress: 0,
      completedCount: 0,
      totalExecutions: 0,
      error: null,
      runId: null,
    }
  );

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Poll for progress updates
  const pollProgress = useCallback(
    async (runId: string, totalExecutions: number) => {
      try {
        const data = await apiRequest(`/api/eval/runs/${runId}`);
        const run = data.run;

        const completedCount = run.completed_executions || 0;
        const progress =
          totalExecutions > 0
            ? Math.round((completedCount / totalExecutions) * 100)
            : 0;

        setExecutionProgress(prev => ({
          ...prev,
          progress,
          completedCount,
        }));

        // Check if completed
        if (run.status === 'completed') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setExecutionProgress(prev => ({
            ...prev,
            state: 'completed',
            progress: 100,
          }));
          setTimeout(() => {
            onSuccess(runId);
          }, 1000);
        } else if (run.status === 'failed') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setExecutionProgress(prev => ({
            ...prev,
            state: 'error',
            error: 'Execution failed. Check server logs for details.',
          }));
        }
      } catch (err: any) {
        logError('Error polling progress', err);
        // Don't stop polling on error - might be transient
      }
    },
    [onSuccess]
  );

  const execute = useCallback(
    async (params: ExecuteParams) => {
      const {
        promptIds,
        configIds,
        runName,
        description,
        multiplicity,
        concurrency,
        promptSetId,
      } = params;

      try {
        const total = promptIds.length * configIds.length * multiplicity;

        // Create run
        setExecutionProgress({
          state: 'creating',
          progress: 0,
          completedCount: 0,
          totalExecutions: total,
          error: null,
          runId: null,
        });

        const { run } = await apiRequest('/api/eval/runs', {
          method: 'POST',
          body: JSON.stringify({
            name: runName,
            description: description || undefined,
            total_executions: total,
          }),
        });

        // Start execution (backend runs in background)
        await apiRequest('/api/eval/execute', {
          method: 'POST',
          body: JSON.stringify({
            run_id: run.id,
            prompt_ids: promptIds,
            config_ids: configIds,
            multiplicity,
            concurrency,
          }),
        });

        // Start running state
        setExecutionProgress(prev => ({
          ...prev,
          state: 'running',
          runId: run.id,
        }));

        // Start polling for progress (every 2 seconds)
        pollingIntervalRef.current = setInterval(() => {
          pollProgress(run.id, total);
        }, 2000);

        // Poll immediately once
        pollProgress(run.id, total);
      } catch (err: any) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setExecutionProgress(prev => ({
          ...prev,
          state: 'error',
          error: err.message,
        }));
      }
    },
    [pollProgress]
  );

  const reset = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setExecutionProgress({
      state: 'idle',
      progress: 0,
      completedCount: 0,
      totalExecutions: 0,
      error: null,
      runId: null,
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  return {
    executionProgress,
    execute,
    reset,
  };
}
